import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Badge, Button, Row, Col } from 'react-bootstrap';
import {
  FaTimes,
  FaUserCircle,
  FaBox,
  FaMapPin,
  FaClock,
  FaRedo,
  FaPaperclip,
} from 'react-icons/fa';
import { BACKEND_URL } from '../config';

// 🟢 Axios instance (Required only if we were fetching inside the modal, but kept for helper functions)
const authAxios = axios.create({
  baseURL: BACKEND_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 🟢 Map order status → Bootstrap badge variant
const getOrderStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case 'delivered':
    case 'completed':
      return 'success';
    case 'pending':
      return 'info';
    case 'in_transit':
    case 'assigned':
      return 'warning';
    case 'cancelled':
    case 'rejected':
    case 'returned':
      return 'danger';
    default:
      return 'secondary';
  }
};

// 🟢 Format date & time
const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return { date: 'N/A', time: '' };
  try {
    const date = new Date(dateTimeString);
    const formattedDate = `${date.getDate().toString().padStart(2, '0')}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`;
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return { date: formattedDate, time: time };
  } catch {
    return { date: 'Invalid Date', time: '' };
  }
};

// 🌟 FIXED: Component now accepts 'order' prop directly 🌟
const AdminOrderDetailsModal = ({ show, handleClose, order }) => {
  // 🛑 REMOVED: Internal state (orders, setOrders, setOrder)
  // 🛑 REMOVED: Internal useEffect for fetching order data

  // If no order object is passed, or the modal isn't visible, return null.
  // The 'order' prop is passed from the parent component (AdminDashboard/AdminOrders).
  if (!order) return null;

  // --- START: Address Helper Functions ---
  // Logic to determine correct address based on order type
  const getPickupAddress = (o) => {
    if (o.type === 'purchase_order') return o.vendor?.address || 'N/A';
    if (o.type === 'sales_order') return o.showroom?.address || 'N/A';
    if (o.type === 'purchase_return') return o.showroom?.address || 'N/A';
    if (o.type === 'sales_return') return o.customer?.address || 'N/A';
    return o.pickup_address || 'N/A'; // Fallback
  };

  const getDropAddress = (o) => {
    if (o.type === 'purchase_order') return o.showroom?.address || 'N/A';
    if (o.type === 'sales_order') return o.customer?.address || 'N/A';
    if (o.type === 'purchase_return') return o.vendor?.address || 'N/A';
    if (o.type === 'sales_return') return o.showroom?.address || 'N/A';
    return o.delivery_address || 'N/A'; // Fallback
  };
  // --- END: Address Helper Functions ---

  const createdDateTime = formatDateTime(order.created_at);
  const updatedDateTime = formatDateTime(order.updated_at);

  // Consolidated list of attachments
  // Note: The parent component should ensure attachments are structured (e.g., order.attachments.pod_photos)
  const attachments = (order.attachments?.pod_photos || [])
    .concat(
      order.attachments?.voice_notes || [],
      order.attachments?.order_docs || []
    )
    .filter((file) => file.path); // Filter out any attachments without a path

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='lg'
      centered
      className='view-order-modal'
      style={{ maxHeight: '90vh' }}
    >
      <Modal.Header
        closeButton
        style={{ padding: '0.75rem 1rem', fontSize: '0.9rem' }}
      >
        <Modal.Title
          as='h5'
          className='w-100 text-center mb-0'
          style={{ fontSize: '1.1rem', fontWeight: '600' }}
        >
          Order Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className='p-2'
        style={{
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          fontSize: '0.85rem',
        }}
      >
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center border-bottom pb-2 mb-2'>
          <h6
            className='mb-0 font-weight-bold text-dark'
            style={{ fontSize: '0.95rem' }}
          >
            Order No: {order.po_number || order.id}
          </h6>
          <Badge
            bg={getOrderStatusVariant(order.status)}
            className='mt-1 mt-md-0 px-2 py-1 rounded-pill text-uppercase'
            style={{ fontSize: '0.7rem', fontWeight: '600' }}
          >
            {order.status?.replace(/_/g, ' ') || '—'}
          </Badge>
        </div>

        <main style={{ fontSize: '0.85rem' }}>
          {/* Customer & Type Section */}
          <section className='bg-light p-2 rounded border border-gray-100 shadow-sm mb-2'>
            <Row className='g-2'>
              <Col
                xs={12}
                className='d-flex justify-content-between align-items-center border-bottom pb-1'
              >
                <span className='text-muted' style={{ fontSize: '0.8rem' }}>
                  Type
                </span>
                <span
                  className='text-dark font-weight-medium text-capitalize'
                  style={{ fontSize: '0.85rem' }}
                >
                  {order.type?.replace(/_/g, ' ') || '—'}
                </span>
              </Col>
              <Col
                xs={12}
                className='d-flex justify-content-between align-items-start border-bottom pt-1 pb-1'
              >
                <span className='text-muted' style={{ fontSize: '0.8rem' }}>
                  Customer/Vendor
                </span>
                <span
                  className='text-right text-dark font-weight-bold'
                  style={{ fontSize: '0.85rem' }}
                >
                  {order.customer?.name || order.vendor?.name || '—'}
                </span>
              </Col>
              <Col
                xs={12}
                className='d-flex justify-content-between align-items-center pt-1'
              >
                <div
                  className='text-muted d-flex align-items-center'
                  style={{ fontSize: '0.8rem' }}
                >
                  <FaUserCircle
                    className='me-1'
                    style={{ fontSize: '0.9rem' }}
                  />
                  Driver
                </div>
                <span
                  className='text-dark font-weight-semibold'
                  style={{ fontSize: '0.85rem' }}
                >
                  {order.driver?.name || 'Unassigned'}
                </span>
              </Col>
            </Row>
          </section>

          {/* Logistics */}
          <section className='bg-light p-2 rounded border border-gray-100 shadow-sm mb-2'>
            <div>
              <div className='border-bottom border-dashed pb-2 mb-2'>
                <div
                  className='text-success mb-1 d-flex align-items-center'
                  style={{ fontSize: '0.8rem' }}
                >
                  <FaBox className='me-1' style={{ fontSize: '0.9rem' }} />
                  Pickup
                </div>
                <p
                  className='text-dark ms-3 mb-0'
                  style={{ fontSize: '0.75rem' }}
                >
                  {getPickupAddress(order)}
                </p>
              </div>
              <div>
                <div
                  className='text-danger mb-1 d-flex align-items-center'
                  style={{ fontSize: '0.8rem' }}
                >
                  <FaMapPin className='me-1' style={{ fontSize: '0.9rem' }} />
                  Drop
                </div>
                <p
                  className='text-dark ms-3 mb-0'
                  style={{ fontSize: '0.75rem' }}
                >
                  {getDropAddress(order)}
                </p>
              </div>
            </div>
          </section>

          {/* Timestamps */}
          <Row className='g-2 mb-2'>
            <Col md={6}>
              <div className='bg-light p-2 rounded border border-gray-100 shadow-sm'>
                <div
                  className='text-muted mb-1 d-flex align-items-center'
                  style={{ fontSize: '0.75rem' }}
                >
                  <FaClock className='me-1' style={{ fontSize: '0.85rem' }} />
                  <span style={{ marginLeft: '4px' }}>Created</span>
                </div>
                <p
                  className='text-dark ms-3 mb-0'
                  style={{ fontSize: '0.85rem', fontWeight: '600' }}
                >
                  {createdDateTime.date}
                </p>
                <p
                  className='text-muted ms-3 mb-0'
                  style={{ fontSize: '0.75rem' }}
                >
                  {createdDateTime.time}
                </p>
              </div>
            </Col>

            <Col md={6}>
              <div className='bg-light p-2 rounded border border-gray-100 shadow-sm'>
                <div
                  className='text-muted mb-1 d-flex align-items-center'
                  style={{ fontSize: '0.75rem' }}
                >
                  <FaRedo className='me-1' style={{ fontSize: '0.85rem' }} />
                  <span style={{ marginLeft: '4px' }}>Last Updated</span>
                </div>
                <p
                  className='text-dark ms-3 mb-0'
                  style={{ fontSize: '0.85rem', fontWeight: '600' }}
                >
                  {updatedDateTime.date}
                </p>
                <p
                  className='text-muted ms-3 mb-0'
                  style={{ fontSize: '0.75rem' }}
                >
                  {updatedDateTime.time}
                </p>
              </div>
            </Col>
          </Row>

          {/* Remarks & Attachments */}
          <section className='pt-2 border-top border-gray-200'>
            <div className='mb-2'>
              <div
                className='bg-gray-100 p-2 rounded text-gray-600'
                style={{ fontSize: '0.8rem' }}
              >
                {order.notes || order.notes || 'No remarks available.'}
              </div>
            </div>
            <div>
              <div
                className='bg-gray-100 p-2 rounded text-gray-600'
                style={{ fontSize: '0.8rem' }}
              >
                {attachments.length > 0 ? (
                  <ul className='list-unstyled mb-0'>
                    {attachments.map((file, idx) => (
                      <li key={idx} className='mb-1'>
                        <a
                          href={`${BACKEND_URL}/${file.path}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='d-flex align-items-center text-primary text-decoration-none'
                          style={{ fontSize: '0.8rem' }}
                        >
                          <FaPaperclip
                            className='me-1'
                            style={{ fontSize: '0.85rem' }}
                          />
                          {file.type?.replace(/_/g, ' ') ||
                            file.name ||
                            `File ${idx + 1}`}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  'No files.'
                )}
              </div>
            </div>
          </section>
        </main>
      </Modal.Body>

      <Modal.Footer className='border-0' style={{ padding: '0.5rem 1rem' }}>
        <Button
          variant='secondary'
          onClick={handleClose}
          size='sm'
          style={{ fontSize: '0.85rem' }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminOrderDetailsModal;
